import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, ArrowRight, Check, Clock, Save, Send,
  X, Pencil, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { LanguagePill, formatTimeLeft } from "@/components/TaskCard";
import { WorkspaceForm } from "@/components/workspace/forms";
import SuccessModal from "@/components/SuccessModal";
import QuestionPreview from "@/components/workspace/QuestionPreview";
import WorkspaceProgressBar from "@/components/workspace/WorkspaceProgressBar";
import InstructionsRenderer, { parseInstructions } from "@/components/workspace/InstructionsRenderer";
import {
  getTask, getWorkspace, saveDraft, saveDraftAnswers, submitWorkspace,
  acknowledgeAuthenticity,
} from "@/lib/afritask";

// ---------------------------------------------------------------------------
// TaskWorkspace — top-level router. Loads task metadata + entry state, then
// routes to <TaskStepper/> when task.questions[] is present, else falls
// back to the legacy <LegacyWorkspace/> single-blob form (unchanged).
// ---------------------------------------------------------------------------
export default function TaskWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me } = useOutletContext();
  const [state, setState] = useState({ loading: true });

  const load = useCallback(async () => {
    try {
      const [task, ws] = await Promise.all([getTask(id), getWorkspace(id)]);
      setState({ loading: false, task, workspace: ws });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403 || status === 404) {
        toast.error(
          e?.response?.data?.detail ||
            "You must have entered this round to open the workspace."
        );
        navigate(`/app/tasks/${id}`);
      } else {
        toast.error("Failed to load workspace");
      }
    }
  }, [id, navigate]);

  useEffect(() => { if (me) load(); }, [me, load]);

  if (state.loading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const hasQuestions = (state.task?.questions?.length || 0) > 0;
  return hasQuestions
    ? <TaskStepper task={state.task} workspace={state.workspace} />
    : <LegacyWorkspace task={state.task} workspace={state.workspace} onSubmitted={load} />;
}

// ---------------------------------------------------------------------------
// TaskStepper — Phase C flow: intro → question 1..N → review → submitted.
// Uses the reusable QuestionPreview widget for every question type.
// ---------------------------------------------------------------------------
function TaskStepper({ task, workspace }) {
  const navigate = useNavigate();
  const questions = useMemo(
    () => (task.questions || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [task.questions]
  );
  const readOnly = !!workspace.read_only;

  const [screen, setScreen] = useState(
    workspace.submission_data?.answers ? "review" : "intro"
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [validationError, setValidationError] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // Phase C2: authenticity acknowledgement. If the entry already has an
  // ack timestamp, the checkbox starts ticked; otherwise the user must tick
  // before Continue is enabled.
  const [authAck, setAuthAck] = useState(
    !!workspace.entry?.authenticity_acknowledged_at
  );
  // Hydrate answers: prefer final submission > autosave draft > legacy draft
  const [answers, setAnswers] = useState(() => {
    const src = workspace.submission_data?.answers
      || workspace.submission_data?.draft_answers
      || workspace.draft_data?.answers
      || {};
    // API stores {q_id: {value}}. UI works with {q_id: value} for terseness.
    const out = {};
    for (const [k, v] of Object.entries(src)) {
      out[k] = v && typeof v === "object" && "value" in v ? v.value : v;
    }
    return out;
  });

  const startedAtRef = useRef(new Date().toISOString());
  const draftDebounce = useRef();

  // --- Draft autosave (500ms debounce) ---
  useEffect(() => {
    if (readOnly || screen === "submitted") return;
    if (draftDebounce.current) clearTimeout(draftDebounce.current);
    draftDebounce.current = setTimeout(async () => {
      try {
        const payload = {};
        for (const [k, v] of Object.entries(answers)) payload[k] = { value: v };
        const r = await saveDraftAnswers(task.task_id, payload);
        setLastSavedAt(r?.saved_at ? new Date(r.saved_at) : new Date());
      } catch { /* silent */ }
    }, 500);
    return () => { if (draftDebounce.current) clearTimeout(draftDebounce.current); };
  }, [answers, readOnly, screen, task.task_id]);

  // --- Per-question validation (mirrors backend `validate_required_answers`) ---
  const validate = useCallback((q, v) => {
    const isEmpty = v == null || v === "" ||
      (Array.isArray(v) && v.length === 0) ||
      (typeof v === "object" && !Array.isArray(v) && Object.keys(v || {}).length === 0);
    if (!q.required && isEmpty) return null;
    if (q.required && isEmpty) return "Answer is incomplete";
    if (q.type === "short_text" || q.type === "long_text") {
      const s = (v || "").trim();
      if (q.min_length && s.length < q.min_length) return `Minimum ${q.min_length} characters`;
      if (q.max_length && s.length > q.max_length) return `Maximum ${q.max_length} characters`;
    }
    if (q.type === "multi_select_card") {
      const arr = Array.isArray(v) ? v : [];
      if (q.min_selections && arr.length < q.min_selections) return `Select at least ${q.min_selections}`;
      if (q.max_selections && arr.length > q.max_selections) return `Select at most ${q.max_selections}`;
    }
    if (q.type === "conditional_single_select" && v?.choice === q.conditional_option) {
      if (!v.text || !v.text.trim()) return "Follow-up text is required";
    }
    return null;
  }, []);

  const missingRequired = useMemo(
    () => questions
      .map((q, i) => ({ q, i, err: validate(q, answers[q.id]) }))
      .filter((x) => x.err),
    [questions, answers, validate]
  );

  const currentQ = questions[currentIndex];
  const currentErr = currentQ ? validate(currentQ, answers[currentQ.id]) : null;

  const setAnswer = (qid, v) => {
    setAnswers((a) => ({ ...a, [qid]: v }));
    setShowValidation(false);
  };

  const goNext = () => {
    if (readOnly) { advance(); return; }
    if (currentErr) { setShowValidation(true); setValidationError(currentErr); return; }
    setValidationError(null); setShowValidation(false);
    advance();
  };
  const advance = () => {
    if (currentIndex < questions.length - 1) setCurrentIndex((i) => i + 1);
    else setScreen("review");
  };
  const goBack = () => {
    if (screen === "review") { setScreen("question"); setCurrentIndex(questions.length - 1); return; }
    if (currentIndex > 0) setCurrentIndex((i) => i - 1);
    else setScreen("intro");
  };
  const jumpTo = (idx) => { setScreen("question"); setCurrentIndex(idx); setShowValidation(false); };

  const handleExit = () => {
    if (readOnly) { navigate("/app/dashboard"); return; }
    setExitConfirmOpen(true);
  };

  const handleSubmit = async () => {
    if (missingRequired.length > 0) {
      toast.error(`Missing ${missingRequired.length} required answer${missingRequired.length === 1 ? "" : "s"}`);
      jumpTo(missingRequired[0].i);
      return;
    }
    try {
      setSubmitting(true);
      const payload = {};
      for (const [k, v] of Object.entries(answers)) payload[k] = { value: v };
      await submitWorkspace(task.task_id, {
        answers: payload,
        started_at: startedAtRef.current,
      });
      setScreen("submitted");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d
        : d?.missing_question_ids ? `Missing ${d.missing_question_ids.length} required answers`
        : "Failed to submit";
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  // ---------------------------- Render ------------------------------------
  if (screen === "submitted") return <SubmittedScreen />;

  return (
    <div className="mx-auto w-full max-w-2xl bg-white pb-24 sm:pb-0" data-testid="workspace-container">
      {/* Top bar */}
      <div className="sticky top-0 z-20 border-b border-neutral-100 bg-white">
        {screen === "question" && (
          <WorkspaceProgressBar current={currentIndex} total={questions.length} />
        )}
        <div className="flex items-center gap-3 px-4 py-3 sm:px-6">
          {screen === "intro" && (
            <button onClick={handleExit}
              className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900"
              data-testid="workspace-exit">
              <ArrowLeft className="h-4 w-4" /> Exit
            </button>
          )}
          {(screen === "question" || screen === "review") && (
            <button onClick={handleExit}
              className="ml-auto rounded-full p-1 text-neutral-400 transition hover:bg-neutral-100 hover:text-neutral-600"
              data-testid="workspace-exit">
              <X className="h-5 w-5" />
            </button>
          )}
          {screen === "intro" && (
            <p className="mx-auto truncate px-2 text-sm font-semibold text-neutral-800">
              {task.title}
            </p>
          )}
          {screen === "intro" && <div className="w-12" />}
        </div>
      </div>

      {/* Body */}
      <div>
        {screen === "intro" && <IntroScreen task={task} questions={questions}
          authAck={authAck}
          onAckChange={async (v) => {
            setAuthAck(v);
            if (v && !readOnly) {
              // Best-effort audit — swallow errors so worker isn't blocked.
              try { await acknowledgeAuthenticity(task.task_id); } catch { /* ignore */ }
            }
          }}
          onContinue={() => setScreen("question")} />}
        {screen === "question" && (
          <QuestionScreen
            q={currentQ}
            index={currentIndex}
            total={questions.length}
            value={answers[currentQ.id]}
            onChange={(v) => setAnswer(currentQ.id, v)}
            error={showValidation ? currentErr : null}
            readOnly={readOnly}
            lastSavedAt={lastSavedAt}
          />
        )}
        {screen === "review" && (
          <ReviewScreen
            task={task}
            questions={questions}
            answers={answers}
            missingRequired={missingRequired}
            onEdit={jumpTo}
            readOnly={readOnly}
          />
        )}
      </div>

      {/* Sticky footer nav — fixed to viewport on mobile only; inline flex on desktop. */}
      <StickyFooter inline={screen === "question" || screen === "review"}>
        {screen === "intro" && (
          <Button onClick={() => setScreen("question")}
            disabled={!authAck && !readOnly}
            className="ml-auto h-12 w-full max-w-sm bg-[#B45309] hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="workspace-continue">
            Continue <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        )}
        {screen === "question" && (
          <>
            <Button variant="outline"
              onClick={goBack}
              className="h-12 flex-1 sm:flex-none sm:min-w-[8rem]"
              data-testid="workspace-back-btn">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
            </Button>
            <Button onClick={goNext}
              className="h-12 flex-1 bg-[#B45309] hover:bg-amber-800 sm:ml-auto sm:flex-none sm:min-w-[10rem]"
              data-testid="workspace-continue">
              {currentIndex === questions.length - 1 ? "Review answers" : "Continue"}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </>
        )}
        {screen === "review" && (
          <>
            <Button variant="outline" onClick={goBack}
              className="h-12 flex-1 sm:flex-none sm:min-w-[8rem]"
              data-testid="workspace-back-btn">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
            </Button>
            {!readOnly && (
              <Button onClick={handleSubmit}
                disabled={submitting || missingRequired.length > 0}
                className="h-12 flex-1 bg-[#B45309] hover:bg-amber-800 sm:ml-auto sm:flex-none sm:min-w-[10rem]"
                data-testid="workspace-submit">
                <Send className="mr-1.5 h-4 w-4" />
                {submitting ? "Submitting…" : "Submit task"}
              </Button>
            )}
          </>
        )}
      </StickyFooter>

      {/* Exit confirmation */}
      <Dialog open={exitConfirmOpen} onOpenChange={setExitConfirmOpen}>
        <DialogContent data-testid="workspace-exit-dialog">
          <DialogHeader>
            <DialogTitle>
              {screen === "intro" ? "Leave without starting?" : "Leave without submitting?"}
            </DialogTitle>
            <DialogDescription>
              {screen === "intro"
                ? "Your entry stays reserved. You can come back anytime before the deadline."
                : "Your draft is saved. You can come back to finish before the deadline."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExitConfirmOpen(false)}
              data-testid="workspace-exit-cancel">Stay</Button>
            <Button onClick={() => navigate("/app/dashboard")}
              className="bg-[#B45309] hover:bg-amber-800"
              data-testid="workspace-exit-confirm">Leave</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StickyFooter({ children, inline }) {
  // Mobile: fixed to viewport bottom (immersive task-workspace, BottomNav is hidden).
  // Desktop (`sm:`): when `inline`, sits right after the content instead of floating.
  const desktop = inline
    ? "sm:static sm:mx-auto sm:mt-6 sm:max-w-2xl sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none sm:backdrop-blur-none"
    : "";
  return (
    <div className={`fixed inset-x-0 bottom-0 z-30 border-t border-neutral-200 bg-white/95 px-4 py-3 shadow-[0_-8px_20px_-8px_rgba(0,0,0,0.1)] backdrop-blur ${desktop}`}>
      <div className="mx-auto flex max-w-2xl items-center gap-3">
        {children}
      </div>
    </div>
  );
}

// ---------------------------- Screen 0: Intro -------------------------------
function IntroScreen({ task, questions, authAck, onAckChange, onContinue }) {
  const estMin = Math.max(1, Math.ceil((questions.length * 30) / 60));
  const rewardNgn = (task.reward_points || 0) * 10;
  const parsed = parseInstructions(task.instructions || "");
  return (
    <div className="relative" data-testid="workspace-intro">
      {/* Subtle terracotta gradient strip behind the Welcome heading */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-amber-50/60 to-transparent" aria-hidden="true" />

      <div className="relative mx-auto max-w-2xl space-y-5 px-4 py-6 sm:px-6 sm:py-8">
        {/* Header */}
        <div>
          <p className="text-center text-[10px] font-semibold uppercase tracking-widest text-amber-700 sm:text-left">
            Task
          </p>
          <h1 className="mt-1 text-center text-3xl font-bold tracking-tight text-neutral-900 sm:text-left sm:text-4xl">
            Welcome
          </h1>
        </div>

        {/* Hero card — title + language pill + meta row */}
        <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5"
          data-testid="workspace-hero-card">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold leading-tight text-neutral-900 sm:text-xl">{task.title}</h2>
            <LanguagePill language={task.language} />
          </div>
          {task.description && (
            <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-neutral-600">
              {task.description}
            </p>
          )}
          <div className="mt-4 grid grid-cols-3 gap-3 border-t border-neutral-100 pt-3">
            <HeroMeta label="Estimated" value={`${estMin} min`} icon={<Clock className="h-3.5 w-3.5" />} />
            <HeroMeta label="Reward"
              value={<span className="font-semibold text-emerald-700">₦{rewardNgn.toLocaleString()}</span>} />
            <HeroMeta label="Questions" value={`${questions.length}`} />
          </div>
        </div>

        {/* Structured instructions */}
        {(parsed.intro || parsed.sections.length > 0) && (
          <InstructionsRenderer parsed={parsed} />
        )}

        {/* Consent line + checkbox */}
        {parsed.consent && (
          <p className="text-sm leading-relaxed text-neutral-600" data-testid="workspace-consent-line">
            {parsed.consent}
          </p>
        )}
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-amber-300 bg-amber-50/50 p-4 transition hover:bg-amber-50"
          data-testid="workspace-authenticity-label">
          <input type="checkbox"
            checked={!!authAck}
            onChange={(e) => onAckChange(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[#B45309]"
            data-testid="workspace-authenticity-checkbox" />
          <span className="text-sm leading-relaxed text-neutral-800">
            I understand and agree — all my answers are my own original work.
          </span>
        </label>

        {/* Mobile-only inline continue (sticky footer still primary CTA) */}
        <div className="sm:hidden">
          <Button onClick={onContinue}
            disabled={!authAck}
            className="h-12 w-full bg-[#B45309] hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="workspace-continue-inline">
            Continue <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function HeroMeta({ label, value, icon }) {
  return (
    <div className="min-w-0">
      <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-neutral-500">
        {icon}{label}
      </p>
      <p className="mt-0.5 truncate text-sm text-neutral-900">{value}</p>
    </div>
  );
}

// ---------------------------- Screen 1..N: Question -------------------------
function QuestionScreen({ q, index, total, value, onChange, error, readOnly, lastSavedAt }) {
  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6" data-testid="workspace-question">
      <div className={`relative rounded-xl border bg-white p-5 shadow-md ${error ? "border-red-300" : "border-neutral-200"}`}>
        {/* Question-number badge, top-left */}
        <span className="absolute -top-2 left-4 rounded-full bg-amber-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm"
          data-testid="workspace-question-badge">
          Q{index + 1}
        </span>
        {error && (
          <div className="mb-3 mt-1 inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-700"
            data-testid="workspace-question-error">
            <AlertTriangle className="h-3.5 w-3.5" /> {error}
          </div>
        )}
        <div className="mb-4 mt-1 flex items-start justify-between gap-3">
          <h3 className="text-lg font-semibold leading-snug text-neutral-900 sm:text-xl">{q.text}</h3>
          <Badge className={q.required
            ? "shrink-0 bg-amber-50 text-amber-800 hover:bg-amber-50"
            : "shrink-0 bg-neutral-100 text-neutral-600 hover:bg-neutral-100"}>
            {q.required ? "Required" : "Optional"}
          </Badge>
        </div>
        <QuestionPreview question={q} value={value} onChange={onChange} disabled={readOnly} />
        {/* Auto-save chip — fades in for 2s after each save, then out. */}
        {lastSavedAt && !readOnly && (
          <DraftSavedChip savedAt={lastSavedAt} />
        )}
      </div>
      <p className="mt-2 text-center text-[11px] text-neutral-400 sm:hidden">
        Question {index + 1} of {total}
      </p>
    </div>
  );
}

function DraftSavedChip({ savedAt }) {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    setVisible(true);
    const t = setTimeout(() => setVisible(false), 2000);
    return () => clearTimeout(t);
  }, [savedAt]);
  return (
    <div
      className={`pointer-events-none mt-4 inline-flex items-center gap-1 text-[11px] text-neutral-500 transition-opacity duration-500 ${visible ? "opacity-100" : "opacity-0"}`}
      data-testid="workspace-draft-saved">
      <Check className="h-3 w-3 text-emerald-600" aria-hidden="true" />
      Auto-saved · just now
    </div>
  );
}

// ---------------------------- Screen N+1: Review ----------------------------
function ReviewScreen({ task, questions, answers, missingRequired, onEdit, readOnly }) {
  return (
    <div className="px-4 py-6 sm:px-6" data-testid="workspace-review">
      <h1 className="text-2xl font-bold text-neutral-900">Review your answers</h1>
      <p className="mt-1 text-sm text-neutral-600">
        You&apos;ve completed {questions.length} question{questions.length === 1 ? "" : "s"}.
        Review before submitting.
      </p>

      {!readOnly && missingRequired.length > 0 && (
        <div className="mt-4 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
          data-testid="workspace-review-missing">
          <span className="inline-flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4" />
            Missing {missingRequired.length} required answer{missingRequired.length === 1 ? "" : "s"}
          </span>
          <button onClick={() => onEdit(missingRequired[0].i)}
            className="text-xs font-medium underline">
            Fix now
          </button>
        </div>
      )}

      <div className="mt-5 space-y-3">
        {questions.map((q, i) => (
          <ReviewRow key={q.id} idx={i} q={q} value={answers[q.id]}
            hasError={missingRequired.some((m) => m.i === i)}
            onEdit={() => onEdit(i)} readOnly={readOnly} />
        ))}
      </div>

      {readOnly && (
        <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <p className="font-semibold">Submitted</p>
          <p className="mt-1 text-xs">
            AfriSelection AI is scoring this round against {task.title}&apos;s ground truth.
            You&apos;ll be notified when results are in.
          </p>
        </div>
      )}
    </div>
  );
}

function ReviewRow({ idx, q, value, hasError, onEdit, readOnly }) {
  return (
    <div className={`rounded-lg border p-3 ${hasError ? "border-red-200 bg-red-50/40" : "border-neutral-200 bg-white"}`}
      data-testid={`workspace-review-row-${idx}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
            Question {idx + 1}{!q.required && " · optional"}
          </p>
          <p className="mt-0.5 text-sm font-medium text-neutral-900">{q.text}</p>
        </div>
        {!readOnly && (
          <button onClick={onEdit}
            className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-amber-700 hover:underline"
            data-testid={`workspace-review-edit-${idx}`}>
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
        )}
      </div>
      <div className="mt-2">{formatAnswer(q, value)}</div>
    </div>
  );
}

function formatAnswer(q, v) {
  const empty = v == null || v === "" || (Array.isArray(v) && v.length === 0) ||
    (typeof v === "object" && !Array.isArray(v) && Object.keys(v || {}).length === 0);
  if (empty) {
    return <p className="text-xs italic text-neutral-400">Not answered</p>;
  }
  if (q.type === "single_select_card" || q.type === "dropdown") {
    return <span className="inline-block rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900">{v}</span>;
  }
  if (q.type === "multi_select_card") {
    return (
      <div className="flex flex-wrap gap-1.5">
        {(v || []).map((x, i) => (
          <span key={i} className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900">{x}</span>
        ))}
      </div>
    );
  }
  if (q.type === "short_text" || q.type === "long_text") {
    return <TruncatedText text={String(v || "")} />;
  }
  if (q.type === "likert_5") {
    const el = q.endpoint_labels || {};
    const label = v === 5 ? (el.high || "Strongly agree")
      : v === 1 ? (el.low || "Strongly disagree")
      : v === 3 ? "Neutral"
      : "";
    return <p className="text-sm text-neutral-800">{v}/5{label && <span className="text-neutral-500"> — {label}</span>}</p>;
  }
  if (q.type === "matrix_agree_disagree") {
    const scale = q.matrix_scale || ["Strongly disagree","Disagree","Neutral","Agree","Strongly agree"];
    return (
      <ul className="space-y-1">
        {Object.entries(v || {}).map(([stmt, val]) => (
          <li key={stmt} className="flex items-start gap-2 text-xs">
            <span className="min-w-0 flex-1 text-neutral-800">{stmt}</span>
            <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-900">
              {scale[Number(val) - 1] || val}
            </span>
          </li>
        ))}
      </ul>
    );
  }
  if (q.type === "conditional_single_select") {
    if (typeof v !== "object") return <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900">{String(v)}</span>;
    return (
      <div className="space-y-1">
        <span className="inline-block rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900">{v.choice}</span>
        {v.text ? <p className="text-xs text-neutral-700">↳ {v.text}</p> : null}
      </div>
    );
  }
  return <p className="text-xs text-neutral-500">{JSON.stringify(v)}</p>;
}

function TruncatedText({ text, limit = 180 }) {
  const [open, setOpen] = useState(false);
  if (text.length <= limit) return <p className="text-sm text-neutral-800">{text}</p>;
  return (
    <div>
      <p className="text-sm text-neutral-800">
        {open ? text : `${text.slice(0, limit)}…`}
      </p>
      <button onClick={() => setOpen((v) => !v)}
        className="mt-1 text-xs font-medium text-amber-700 hover:underline">
        {open ? "Show less" : "Show more"}
      </button>
    </div>
  );
}

// ---------------------------- Screen N+2: Submitted -------------------------
function SubmittedScreen() {
  const navigate = useNavigate();
  return (
    <div className="mx-auto flex min-h-[calc(100vh-6rem)] w-full max-w-md flex-col items-center justify-center px-6 text-center"
      data-testid="workspace-submitted">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-50">
        <CheckCircle2 className="h-10 w-10 text-[#B45309]" strokeWidth={1.8} />
      </div>
      <h1 className="mt-5 text-2xl font-bold text-neutral-900">Task submitted</h1>
      <p className="mt-2 text-sm leading-relaxed text-neutral-600">
        Your answers are being graded by AfriSelection AI. You&apos;ll be notified
        when the round is ranked.
      </p>
      <Button onClick={() => navigate("/app/dashboard")}
        className="mt-6 h-12 w-full bg-[#B45309] hover:bg-amber-800"
        data-testid="workspace-back-to-dashboard">
        Back to dashboard
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LegacyWorkspace — unchanged single-blob wizard for legacy tasks that
// do NOT define questions[]. Preserves the pre-Phase-C UX exactly.
// ---------------------------------------------------------------------------
const LEGACY_STEPS = [
  { key: "brief", label: "Brief" },
  { key: "task", label: "Task" },
  { key: "review", label: "Review & submit" },
];

function LegacyWorkspace({ task, workspace, onSubmitted }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const readOnly = !!workspace.read_only;
  const entry = workspace.entry;

  const [value, setValue] = useState(
    workspace.submission_data || workspace.draft_data || {}
  );
  const [step, setStep] = useState(readOnly ? 2 : 0);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const dirtyRef = useRef(false);

  useEffect(() => {
    if (readOnly) return;
    const t = setInterval(async () => {
      if (!dirtyRef.current) return;
      try {
        await saveDraft(id, value);
        dirtyRef.current = false;
        setLastSaved(new Date());
      } catch { /* ignore */ }
    }, 30000);
    return () => clearInterval(t);
  }, [id, value, readOnly]);

  const handleChange = (v) => { setValue(v); dirtyRef.current = true; };
  const handleSaveDraft = async () => {
    try {
      await saveDraft(id, value);
      dirtyRef.current = false;
      setLastSaved(new Date());
      toast.success("Draft saved");
    } catch { toast.error("Failed to save draft"); }
  };

  const validateBeforeSubmit = () => {
    const t = task.task_type;
    const v = value || {};
    if (t === "prompt_rating" || t === "voice_rating") {
      const criteria = task.task_payload?.criteria || [];
      const ratings = v.ratings || {};
      const missing = criteria.filter((c) => !ratings[c] || ratings[c] < 1);
      if (missing.length > 0) return `Please rate all ${criteria.length} criteria (missing: ${missing.join(", ")}).`;
      if (!v.overall || v.overall < 1) return "Please give an Overall rating (1-5 stars).";
    } else if (t === "text_classification") {
      if (!v.selected_label) return "Please pick a label.";
    } else if (t === "image_labeling") {
      const tags = v.tags || [];
      const min = task.task_payload?.min_tags || 3;
      if (tags.length < min) return `Please add at least ${min} tags.`;
    } else if (t === "translation") {
      const txt = (v.translation || "").trim();
      const min = task.task_payload?.min_chars || 20;
      if (txt.length < min) return `Translation must be at least ${min} characters.`;
    } else if (t === "audio_transcription") {
      if (!(v.transcription || "").trim()) return "Please type your transcription.";
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validateBeforeSubmit();
    if (err) { toast.error(err); setConfirmOpen(false); return; }
    try {
      setSubmitting(true);
      await submitWorkspace(id, value);
      setConfirmOpen(false);
      setSuccessOpen(true);
      onSubmitted?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to submit");
    } finally { setSubmitting(false); }
  };

  const timeLeft = formatTimeLeft(task.deadline);
  const closingSoon = timeLeft.includes("m") || timeLeft === "closed";

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4 p-3 pb-24 sm:space-y-6 sm:p-0 sm:pb-28">
      <button onClick={() => navigate("/app/applications")}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-neutral-600 hover:text-neutral-900 sm:text-sm"
        data-testid="workspace-back">
        <ArrowLeft className="h-4 w-4" /> Back to applications
      </button>

      <div className="space-y-2 sm:space-y-3">
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          <LanguagePill language={task.language} />
          <Badge variant="outline" className="text-[10px]">{task.task_type.replace(/_/g, " ")}</Badge>
          <Badge className={closingSoon
            ? "bg-red-100 text-red-800 hover:bg-red-100"
            : "bg-neutral-100 text-neutral-800 hover:bg-neutral-100"}>
            <Clock className="mr-1 h-3 w-3" />
            {timeLeft === "closed" ? "Closed" : `By ${timeLeft}`}
          </Badge>
          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px] sm:text-xs">
            Up to {task.reward_points} pts
          </Badge>
          {readOnly && <Badge variant="outline">{entry?.status === "paid" ? "Paid" : "Submitted"}</Badge>}
        </div>
        <h1 className="text-lg font-bold leading-tight tracking-tight text-neutral-900 sm:text-2xl md:text-3xl"
          data-testid="workspace-title">
          {task.title}
        </h1>
      </div>

      <LegacyStepper current={step} onJump={(i) => (readOnly || i <= step ? setStep(i) : null)} />

      {step === 0 && <LegacyBriefStep task={task} />}
      {step === 1 && (
        <div data-testid="workspace-form">
          <WorkspaceForm
            task_type={task.task_type}
            payload={task.task_payload || {}}
            value={value}
            onChange={handleChange}
            readOnly={readOnly}
          />
        </div>
      )}
      {step === 2 && <LegacyReviewStep task={task} value={value} readOnly={readOnly} entry={entry} />}

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-neutral-200 bg-white/95 p-2 backdrop-blur sm:p-4">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-2 sm:gap-3">
          <div className="hidden min-w-0 text-xs text-neutral-500 sm:block">
            {readOnly ? "Submission locked — view only"
              : lastSaved ? `Draft saved ${lastSaved.toLocaleTimeString()}`
              : "Auto-saves every 30s while you type"}
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            {step > 0 && (
              <Button variant="ghost" onClick={() => setStep(step - 1)} className="h-10 sm:h-11" data-testid="wizard-back">
                <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Back
              </Button>
            )}
            {!readOnly && step === 1 && (
              <Button variant="outline" onClick={handleSaveDraft} className="h-10 sm:h-11" data-testid="save-draft-btn">
                <Save className="mr-1.5 h-3.5 w-3.5" /> Save
              </Button>
            )}
            {step < 2 ? (
              <Button onClick={() => setStep(step + 1)} className="h-10 bg-amber-700 hover:bg-amber-800 sm:h-11"
                data-testid="wizard-next">
                Next <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            ) : (!readOnly && (
              <Button onClick={() => setConfirmOpen(true)} className="h-10 bg-amber-700 hover:bg-amber-800 sm:h-11"
                data-testid="submit-btn">
                <Send className="mr-1.5 h-3.5 w-3.5" /> Submit
              </Button>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit for AI grading?</DialogTitle>
            <DialogDescription>
              Once submitted, you cannot edit. AfriSelection AI will score your work
              against the ground truth, then the admin ranks the round and pays the top scorers.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-amber-700 hover:bg-amber-800"
              data-testid="confirm-submit-btn">
              {submitting ? "Submitting…" : "Yes, submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SuccessModal
        open={successOpen}
        onOpenChange={(v) => { setSuccessOpen(v); if (!v) navigate("/app/applications"); }}
        variant="success"
        title="Submission received"
        body="AfriSelection AI will review after the round closes. You'll get a message when results are in."
        ctaLabel="Back to dashboard"
        onCta={() => navigate("/app/dashboard")}
        testid="task-submitted-modal"
      />
    </div>
  );
}

function LegacyStepper({ current, onJump }) {
  return (
    <ol className="flex items-center gap-1 sm:gap-2" data-testid="wizard-stepper">
      {LEGACY_STEPS.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li key={s.key} className="flex flex-1 items-center gap-1 sm:gap-2">
            <button type="button" onClick={() => onJump(i)}
              className={`flex items-center gap-1.5 rounded-full px-2 py-1 text-[11px] font-semibold transition sm:gap-2 sm:px-3 sm:py-1.5 sm:text-xs ${
                active ? "bg-amber-700 text-white"
                : done ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
              }`}
              data-testid={`wizard-step-${s.key}`}>
              <span className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-[9px] tabular-nums sm:h-5 sm:w-5 sm:text-[10px] ${
                active ? "bg-white text-amber-700" : done ? "bg-emerald-600 text-white" : "bg-white text-neutral-500"
              }`}>
                {done ? <Check className="h-2.5 w-2.5 sm:h-3 sm:w-3" /> : i + 1}
              </span>
              <span className="hidden sm:inline">{s.label}</span>
              <span className="sm:hidden">{s.key === "review" ? "Review" : s.label.split(" ")[0]}</span>
            </button>
            {i < LEGACY_STEPS.length - 1 && (
              <span className={`h-px flex-1 ${i < current ? "bg-emerald-300" : "bg-neutral-200"}`} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function LegacyBriefStep({ task }) {
  const p = task.task_payload || {};
  return (
    <Card data-testid="wizard-brief">
      <CardContent className="space-y-3 p-4 sm:space-y-4 sm:p-6">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-500 sm:text-xs">Brief</p>
          <p className="mt-2 text-sm leading-relaxed text-neutral-800">
            {p.description || task.description || "Complete the task on the next step."}
          </p>
        </div>
        <div className="rounded-lg border border-amber-100 bg-amber-50/60 p-3 text-sm text-amber-900 sm:p-4">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-amber-800 sm:text-xs">
            How AfriSelection AI grades
          </p>
          <ol className="ml-4 list-decimal space-y-1 text-xs leading-relaxed text-amber-900/90">
            <li>Complete the task truthfully.</li>
            <li>Submit before the deadline.</li>
            <li>AI grades every submission 0–100.</li>
            <li>Top scorers get paid.</li>
          </ol>
        </div>
      </CardContent>
    </Card>
  );
}

function LegacyReviewStep({ task, value, readOnly, entry }) {
  const isEmpty = !value || Object.keys(value).length === 0;
  return (
    <Card data-testid="wizard-review">
      <CardContent className="space-y-4 p-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-neutral-500">
            Review your submission
          </p>
          <p className="mt-1 text-sm text-neutral-700">
            {readOnly ? "This is your final submission. You cannot edit it."
              : "Double-check your work below. Once you submit, you cannot edit."}
          </p>
        </div>
        {isEmpty ? (
          <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            You haven&apos;t entered any answers yet. Go back to the Task step to fill in your work.
          </div>
        ) : (
          <pre className="max-h-72 overflow-auto rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-800"
            data-testid="wizard-review-json">
            {JSON.stringify(value, null, 2)}
          </pre>
        )}
        {readOnly && entry?.ai_score != null && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
            <p className="font-semibold">AI score: {entry.ai_score}/100</p>
            {entry.ai_explanation && (
              <p className="mt-1 text-xs leading-relaxed">{entry.ai_explanation}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
